import { Box } from "@chakra-ui/react";
import moment from "moment";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AppInput from "../../components/Core/AppInput/AppInput";
import Button from "../../components/Core/Button/Button";
import { DropdownController } from "../../components/Core/Dropdown/Dropdown";
import Flex from "../../components/Layout/Flex";
import { EmploymentAndSalaryController } from "../../components/Shared/EmploymentAndSalary/EmploymentAndSalary";
import { UpdateSalaryForm } from "../../components/Shared/SalaryForm/SalaryForm";
import { BANKS } from "../../configs/banks.configs";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import Consultant, {
	Bank,
	Employment,
	Manager,
	UpdateConsultantRequest,
} from "../../models/Consultant";
import { KeyValue } from "../../models/KeyValue";
import User from "../../models/User";
import { useUpdateConsultant } from "../../networking/consultant.hooks";
import { useInternalUsers } from "../../networking/internal-user.hooks";

type ConsultantsEmpAndSalaryInputs = {
	profile: {
		employment: Employment;
		bank: Bank;
		manager: Manager;
	};
};

const ConsultantsEmpAndSalaryTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const { mutate: updateConsultant } = useUpdateConsultant();
	// Slim internal-user list for the manager dropdown (used to be prefetched
	// into usersStore by the details page).
	const { users } = useInternalUsers({ page: 0, keyword: "", view: "select_option" });
	const { t } = useTranslation();
	const managerList = useMemo(
		() =>
			(users as unknown as User[]).map((u) => ({
				key: u.id,
				value:
					u.firstName && u.lastName
						? `${u.firstName} ${u.lastName}${u.isDeleted ? ` (${t("DELETED")})` : ""}`
						: "",
			})),
		[users, t]
	);

	const { control, handleSubmit, reset, register, formState } =
		useForm<ConsultantsEmpAndSalaryInputs>({
			defaultValues: {
				...consultant,
				profile: { manager: { id: "" }, bank: { name: "" }, ...consultant.profile },
			},
		});

	useEffect(() => {
		reset(consultant);
	}, [consultant, reset]);

	useUnsavedChangesInFormChecker(formState);

	const onSubmit = handleSubmit(async (e) => {
		if (e.profile.employment) {
			const updatedEmployment: Employment = {
				union: e.profile.employment.union,
				employmentOption: e.profile.employment.employmentOption,
				collectiveAgreement: e.profile.employment.collectiveAgreement,
				colAgreement: e.profile.employment.colAgreement,
				title: e.profile.employment.title,
				started: moment(e.profile.employment.started).toISOString(),
				ended: e.profile.employment.ended
					? moment(e.profile.employment.ended).toISOString()
					: undefined,
				firstStarted: moment(e.profile.employment.firstStarted).toISOString(),
			};

			const updateConsultantReq: UpdateConsultantRequest = {
				profile: {
					employment: updatedEmployment,
					bank: {
						name: e.profile.bank.name,
						clearingNumber: e.profile.bank.clearingNumber,
						accountNumber: e.profile.bank.accountNumber,
					},
					manager: { id: e.profile.manager.id },
				},
			};

			if (!updateConsultantReq.profile?.manager?.id) delete updateConsultantReq.profile?.manager;

			await updateConsultant({
				userId: consultant.id,
				update: updateConsultantReq,
			});
		}
	});

	return (
		<Box display={"flex"} flexDir={"row"} style={{ maxWidth: 1100, marginTop: 25 }}>
			<Box flex="2" mr="8">
				<UpdateSalaryForm consultantId={consultant.id} showHistory />
			</Box>
			<Box flex="3" borderLeft={"2px"} borderColor={"#ccc"} pl="8">
				<form onSubmit={onSubmit}>
					<EmploymentAndSalaryController control={control} name="profile.employment" />
					<Flex direction="column" margin="1.5rem 0 0 0" padding="0 1rem 0 0">
						<DropdownController<KeyValue>
							control={control}
							name="profile.bank.name"
							options={BANKS}
							label="Bank"
							getValue={(o) => o.key as string}
							getOptionLabel={(option: KeyValue) => option.value}
						/>
					</Flex>

					<Flex padding="0 1rem 0 0">
						<AppInput
							{...register("profile.bank.clearingNumber")}
							label="CLEARING_NO"
							width="147px"
							margin="0 1rem 0 0"
						/>
						<AppInput {...register("profile.bank.accountNumber")} label="BANK_ACCOUNT" />
					</Flex>
					<Flex padding="0 1rem 0 0" direction="column">
						<DropdownController<KeyValue>
							control={control}
							name="profile.manager.id"
							options={managerList}
							label="ORGANISATION_CONSULTANT_MANAGER"
							getValue={(o) => o.key as string}
							getOptionLabel={(option: KeyValue) => option.value}
						/>
					</Flex>
					<Flex justifyContent="center" margin="2.5rem 0 0 0">
						<Button width="9.75rem">{t("SAVE_CHANGES")}</Button>
					</Flex>
				</form>
			</Box>
		</Box>
	);
};

export default ConsultantsEmpAndSalaryTab;
